import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  ACTION_SET_LANGUAGE,
  ACTION_SET_THEME,
  ACTION_UPLOAD_FILES,
  ACTION_GET_TICKET_LIST_CONFIG,
  ACTION_GET_TICKETS_LIST,
  ACTION_SYNC_TICKERS,
  ACTION_SIGN_UP_USER,
  ACTION_COMPLETE_PROFILE,
  ACTION_SIGN_IN_USER,
  ACTION_GET_USER,
  ACTION_UPDATE_USER,
  ACTION_UPDATE_EMAIL,
  ACTION_DELETE_ACCOUNT,
  ACTION_LOGOUT,
  ACTION_CLEAR_USER,
  ACTION_GET_SUBSCRIPTIONS,
  ACTION_DELETE_TICKET,
  // ACTION_UPDATE_USER_SUBSCRIPTION,
  ACTION_UPDATE_TICKET,
} from "../Actions";
import { supabase } from "../../lib/supabase";
import { saveTickersToIDB } from "../../db/stocksDB";
import i18next from "i18next";
import { getCurrentUserId } from "../../services/supabase/getCurrentUserId";

export const setLanguage = createAsyncThunk(
  ACTION_SET_LANGUAGE.type,
  async (args) => {
    try {
      const id = await getCurrentUserId();
      await supabase.from("profiles").update({ language: args }).eq("id", id);
      return args;
    } catch (error) {
      return args;
    }
  }
);

export const setTheme = createAsyncThunk(
  ACTION_SET_THEME.type,
  async (args) => {
    try {
      const id = await getCurrentUserId();
      await supabase.from("profiles").update({ theme: args }).eq("id", id);
      return args;
    } catch (error) {
      return args;
    }
  }
);

export const uploadFiles = createAsyncThunk(
  ACTION_UPLOAD_FILES.type,
  async (args) => {
    return args;
  }
);

export const getTicketListConfig = createAsyncThunk(
  ACTION_GET_TICKET_LIST_CONFIG,
  async (args = {}, { rejectWithValue }) => {
    try {
      const {
        createVersion = null,
        cardViewVersion = null,
        fullViewVersion = null,
      } = args?.versions || {};

      const { data, error } = await supabase.functions.invoke(
        "get-ticket-config",
        {
          body: {
            createVersion,
            cardVersion: cardViewVersion,
            fullVersion: fullViewVersion,
          },
        }
      );

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || "An error occurred");
    }
  }
);

export const getTicketsList = createAsyncThunk(
  ACTION_GET_TICKETS_LIST.type,
  async (args, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase.from("tickets").select("*");
      if (error) throw error;
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const syncTickers = createAsyncThunk(
  ACTION_SYNC_TICKERS.type,
  async (_, { dispatch, rejectWithValue, getState }) => {
    try {
      const lastModified = getState().persist.lastSyncedAt;

      let page = 0;
      let hasMore = true;
      let serverSyncedAt = null;
      let totalSaved = 0;

      while (hasMore) {
        const { data: responseData, error: funcError } =
          await supabase.functions.invoke("get-tickers", {
            body: {
              last_modified: lastModified,
              page: page,
              limit: 1000,
            },
          });

        if (funcError) throw new Error(funcError.message);

        const { data, nextPage, syncedAt, error: logicError } = responseData;

        if (logicError) throw new Error(logicError);

        if (data && data.length > 0) {
          await saveTickersToIDB(data);

          totalSaved += data.length;
        }

        if (syncedAt) serverSyncedAt = syncedAt;

        if (nextPage !== null) {
          page = nextPage;
        } else {
          hasMore = false;
        }
      }

      return { totalSaved, syncedAt: serverSyncedAt };
    } catch (err) {
      return rejectWithValue(err.message || "Unknown Error");
    }
  }
);

export const signUpUser = createAsyncThunk(
  ACTION_SIGN_UP_USER.type,
  async (args, { rejectWithValue, getState }) => {
    const t = i18next.t;
    try {
      const { email, password } = args;
      const { error: authError } = await supabase.auth.signUp({
        email,
        password,
      });
      if (authError) {
        throw new Error(t("pages.auth.signup.errorSignUpMessage"));
      }
      return { success: true, message: t("pages.auth.signup.successMessage") };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const completeProfile = createAsyncThunk(
  ACTION_COMPLETE_PROFILE.type,
  async (args, { rejectWithValue }) => {
    const t = i18next.t;
    try {
      const { data, error } = await supabase.rpc("complete_registration", {
        first_name_input: args.firstName,
        last_name_input: args.lastName,
      });

      if (error || (data && !data.success)) {
        throw new Error(
          t("pages.auth.completeProfile.errorCompleteProfileMessage")
        );
      }
      return {
        success: true,
        message: t("pages.auth.completeProfile.successCompleteProfileMessage"),
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const signInUser = createAsyncThunk(
  ACTION_SIGN_IN_USER.type,
  async (args, { rejectWithValue, dispatch }) => {
    const t = i18next.t;
    try {
      const { email, password } = args;
      const { error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (authError) throw new Error(t("pages.auth.signin.errorSignInMessage"));
      await dispatch(getUser()).unwrap();
      return {
        success: true,
        message: t("pages.auth.signin.successSignInMessage"),
      };
    } catch (error) {
      return rejectWithValue(error.message || "Unknown Error");
    }
  }
);

export const getUser = createAsyncThunk(
  ACTION_GET_USER.type,
  async (_, { rejectWithValue }) => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) return { userProfile: null };

      const { data: rawProfile, error: userProfileError } = await supabase
        .from("profiles")
        .select(
          `
          *,
          user_subscriptions (
            status,
            plan:plans (*)
          )
        `
        )
        .eq("id", user.id)
        .maybeSingle();

      if (userProfileError) {
        throw new Error(userProfileError.message);
      }

      const activeSubscription = rawProfile.user_subscriptions?.find(
        (sub) => sub.status === "active"
      );

      const activePlan = activeSubscription ? activeSubscription.plan : null;
      const userProfile = {
        ...rawProfile,
        current_plan: activePlan,
      };
      delete userProfile.user_subscriptions;

      return { success: true, user, userProfile };
    } catch (error) {
      return rejectWithValue(error.message || "Unknown Error");
    }
  },
  {
    condition: (_, { getState }) => {
      const { root } = getState();
      if (root.loadingAuth) {
        return false;
      }
    },
  }
);

export const updateUser = createAsyncThunk(
  ACTION_UPDATE_USER.type,
  async (args, { rejectWithValue, getState }) => {
    try {
      const id = await getCurrentUserId();
      const { data, error } = await supabase
        .from("profiles")
        .update(args)
        .eq("id", id);
      if (error) {
        throw new Error(error.message || "Unknown Error");
      }
      return { success: true, data: data };
    } catch (error) {
      return rejectWithValue(error.message || "Unknown Error");
    }
  }
);

export const updateEmail = createAsyncThunk(
  ACTION_UPDATE_EMAIL.type,
  async (args, { rejectWithValue }) => {
    try {
      await supabase.auth.updateUser({
        email: args.email,
      });
    } catch (error) {
      return rejectWithValue(error.message || "Unknown Error");
    }
  }
);

export const deleteAccount = createAsyncThunk(
  ACTION_DELETE_ACCOUNT.type,
  async (_, { rejectWithValue }) => {
    try {
      await supabase.functions.invoke("delete-user");
    } catch (error) {
      return rejectWithValue(error.message || "Unknown Error");
    }
  }
);

export const logout = createAsyncThunk(
  ACTION_LOGOUT.type,
  async (_, { rejectWithValue }) => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      return { success: true };
    } catch (error) {
      return rejectWithValue(error.message || "Unknown Error");
    }
  }
);

export const clearUser = createAsyncThunk(
  ACTION_CLEAR_USER.type,
  async () => {
    return { success: true };
  }
);

export const getSubscriptions = createAsyncThunk(
  ACTION_GET_SUBSCRIPTIONS,
  async (_, { rejectWithValue }) => {
    try {
      const { data, error } = await supabase
        .from("plans")
        .select("*")
        .eq("is_active", true)
        .order("price_monthly", { ascending: true });

      if (error) {
        throw new Error(error.message);
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message || "Unknown Error");
    }
  }
);

export const deleteTicket = createAsyncThunk(
  ACTION_DELETE_TICKET.type,
  async (args, { rejectWithValue }) => {
    try {
      let isLocal = false;
      await getCurrentUserId()
        .then(async () => {
          const { error } = await supabase
            .from("tickets")
            .delete()
            .eq("id", args);
          if (error) throw error;
        })
        .catch((error) => {
          isLocal = true;
        });

      return { success: true, data: args, isLocal };
    } catch (error) {
      return rejectWithValue(error.message || "Unknown Error");
    }
  }
);

export const updateTicket = createAsyncThunk(
  ACTION_UPDATE_TICKET.type,
  async (
    { formData, versions, ticketId = null },
    { rejectWithValue, getState }
  ) => {
    let isLocal = false;
    let dataToReturn = null;
    let userId = null;

    try {
      try {
        userId = await getCurrentUserId();
      } catch (err) {
      }

      if (userId) {
        const { data, error } = await supabase.functions.invoke("save-ticket", {
          body: {
            content: formData,
            config_versions: versions,
            ticket_id: ticketId,
          },
        });

        if (error) {
          let errorBody = {};
          try {
            errorBody = JSON.parse(error.message);
          } catch (e) {
            errorBody = { message: error.message };
          }

          if (errorBody.error === "PLAN_LIMIT_REACHED") {
            return rejectWithValue({
              type: "PLAN_LIMIT",
              message: errorBody.message,
            });
          }
          throw new Error(errorBody.message || "Network error");
        }

        if (data?.error) {
          if (data.error === "PLAN_LIMIT_REACHED") {
            return rejectWithValue({
              type: "PLAN_LIMIT",
              message: data.message,
            });
          }
          throw new Error(data.error);
        }

        dataToReturn = data.ticket;
      }

      else {
        const t = i18next.t;

        const state = getState();
        const localTicketsList =
          state.persist.ticketsList || [];
        const isNewTicket = !ticketId;

        if (localTicketsList.length >= 1 && isNewTicket) {
          return rejectWithValue({
            type: "PLAN_LIMIT",
            message: t("pages.ticketsList.succededPlanLimitMessage"),
          });
        }

        isLocal = true;
        const id = ticketId ? ticketId : crypto.randomUUID();
        dataToReturn = {
          content: formData,
          config_versions: versions,
          id,
        };
      }

      return { success: true, data: dataToReturn, isLocal };
    } catch (error) {
      return rejectWithValue({
        type: "GENERAL",
        message: error.message || "An unexpected error occurred",
      });
    }
  }
);
